import pako from 'pako';
import { sharePlanViaApi } from './sharing';
import config from '../config';

/**
 * Utility functions for storing and retrieving race plans from localStorage
 */

/**
 * Helper function to get Authorization headers for authenticated requests
 * @param {Function} getIdToken - Function to get Firebase ID token
 * @returns {Object} - Headers object with Authorization header if authenticated
 */
export const getAuthHeaders = async (getIdToken) => {
  if (!getIdToken) {
    return {};
  }
  
  try {
    const token = await getIdToken();
    if (token) {
      return {
        'Authorization': `Bearer ${token}`
      };
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  
  return {};
};

/**
 * Reduce the number of elevation points while maintaining the overall profile shape
 * @param {Array} elevationData - Original elevation data array
 * @param {number} targetPoints - Target number of points to reduce to
 * @returns {Array} - Reduced elevation data array
 */
const reduceElevationPoints = (elevationData, targetPoints = 1000) => {
  if (!Array.isArray(elevationData) || elevationData.length <= targetPoints) {
    return elevationData;
  }

  const step = Math.floor(elevationData.length / targetPoints);
  const reduced = [];
  
  // Always keep first and last points
  reduced.push(elevationData[0]);
  
  // Sample points at regular intervals
  for (let i = step; i < elevationData.length - 1; i += step) {
    reduced.push(elevationData[i]);
  }
  
  // Always keep last point
  reduced.push(elevationData[elevationData.length - 1]);
  
  return reduced;
};

/**
 * Optimize GPX data for storage by flattening and reducing points
 * @param {Object} gpxData - The original GPX data object
 * @returns {Object} - Optimized GPX data
 */
const optimizeGPXData = (gpxData) => {
  if (!gpxData || !Array.isArray(gpxData.points)) {
    return null;
  }

  // Keep only essential GPX data
  const optimized = {
    points: gpxData.points.map(point => ({
      lat: point.lat,
      lon: point.lon,
      ele: point.ele,
      // Only keep distance if it exists
      ...(point.distance !== undefined && { distance: point.distance }),
      // Only keep cumulativeDistance if it exists
      ...(point.cumulativeDistance !== undefined && { cumulativeDistance: point.cumulativeDistance })
    })),
    // Keep metadata if it exists
    ...(gpxData.metadata && {
      metadata: {
        name: gpxData.metadata.name,
        desc: gpxData.metadata.desc,
        time: gpxData.metadata.time,
        bounds: gpxData.metadata.bounds
      }
    })
  };

  // If we have too many points, reduce them
  if (optimized.points.length > 1000) {
    optimized.points = reduceElevationPoints(optimized.points, 1000);
  }

  return optimized;
};

/**
 * Save a race plan to localStorage
 * 
 * @param {Object} racePlan - The race plan data to save
 * @param {Object} racePlan.formData - The form data
 * @param {Object} racePlan.nutritionPlan - The calculated nutrition plan
 * @param {Object} racePlan.paces - The calculated paces
 * @returns {boolean} - Whether the save was successful
 */
export const saveRacePlan = (racePlan) => {
  try {
    // Check if plan has an ID, if not create one
    const planId = racePlan.id || `plan_${Date.now()}`;
    const currentDate = new Date();
    const timestamp = currentDate.toISOString();
    
    // Create a plan object with metadata - make a copy to avoid modifying the original
    const planToSave = {
      id: planId,
      timestamp: timestamp,
      savedAt: timestamp,
      formData: {
        ...racePlan.formData,
        // Keep elevation data as it's important for the user
        elevationData: racePlan.formData.elevationData,
        // Remove GPX data to save space
        gpxData: null,
        // Add a note about GPX data being removed
        _storageNote: "GPX data has been removed to save storage space. You can still view the elevation profile, but route visualization will not be available when loading this saved plan."
      },
      nutritionPlan: racePlan.nutritionPlan,
      paces: racePlan.paces
    };
    
    console.log(`Saving plan "${planToSave.formData.raceName || 'Unnamed Race'}" with ID ${planId}`);
    
    // Get existing plans
    const existingPlans = JSON.parse(localStorage.getItem('racePlans') || '[]');
    
    // Check if a plan with this ID already exists and remove it
    const filteredPlans = existingPlans.filter(plan => plan.id !== planId);
    
    // Add new plan to the beginning of the array
    const updatedPlans = [planToSave, ...filteredPlans];
    
    // Keep only the last 5 plans to avoid storage issues
    const limitedPlans = updatedPlans.slice(0, 5);
    
    // Check if the plan has large elevation data that should be compressed
    if (planToSave.formData?.elevationData && 
        Array.isArray(planToSave.formData.elevationData) && 
        planToSave.formData.elevationData.length > 1000) {
      
      console.log(`Processing large elevation data array (${planToSave.formData.elevationData.length} points)`);
      
      // First reduce the number of points
      const reducedData = reduceElevationPoints(planToSave.formData.elevationData);
      console.log(`Reduced to ${reducedData.length} points`);
      
      // Convert to JSON and compress
      const elevationJson = JSON.stringify(reducedData);
      const compressed = pako.deflate(elevationJson, { to: 'string' });
      
      // Store compressed data with a flag to indicate compression
      planToSave.formData._compressedElevationData = compressed;
      planToSave.formData._elevationCompressed = true;
      planToSave.formData._elevationLength = reducedData.length;
      
      // Remove the original elevation data to save space
      delete planToSave.formData.elevationData;
      
      console.log(`Compressed elevation data to ${compressed.length} bytes`);
    }
    
    // Calculate size for logging
    const jsonString = JSON.stringify(limitedPlans);
    const sizeInKB = (jsonString.length / 1024).toFixed(2);
    
    // Try to save to localStorage
    try {
      localStorage.setItem('racePlans', jsonString);
      console.log(`Saved plan "${planToSave.formData.raceName || 'Unnamed Race'}" (ID: ${planId}). Storage used: ${sizeInKB} KB`);
      return true;
    } catch (storageError) {
      if (storageError.name === 'QuotaExceededError') {
        // If quota exceeded, try to save with even fewer plans
        const minimalPlans = [planToSave];
        try {
          localStorage.setItem('racePlans', JSON.stringify(minimalPlans));
          console.log(`Saved plan with minimal storage (${minimalPlans.length} plan)`);
          return true;
        } catch (retryError) {
          console.error('Failed to save even with minimal storage:', retryError);
          return false;
        }
      }
      throw storageError;
    }
  } catch (error) {
    console.error('Error saving race plan to localStorage:', error);
    return false;
  }
};

/**
 * Load all saved race plans from localStorage
 * 
 * @returns {Array} - Array of saved race plans
 */
export const loadRacePlans = () => {
  try {
    const plans = JSON.parse(localStorage.getItem('racePlans') || '[]');
    console.log(`Loaded ${plans.length} race plans from localStorage`);
    return plans;
  } catch (error) {
    console.error('Error loading race plans from localStorage:', error);
    return [];
  }
};

/**
 * Load a specific race plan by ID
 * 
 * @param {string} id - The ID of the race plan to load
 * @returns {Object|null} - The race plan or null if not found
 */
export const loadRacePlanById = (id) => {
  try {
    const plans = loadRacePlans();
    const plan = plans.find(p => p.id === id);
    
    if (plan) {
      // Check if plan has compressed elevation data that needs to be decompressed
      if (plan.formData?._elevationCompressed && plan.formData?._compressedElevationData) {
        try {
          console.log(`Decompressing elevation data (${plan.formData._elevationLength} points)`);
          
          // Decompress the data
          const decompressed = pako.inflate(plan.formData._compressedElevationData, { to: 'string' });
          plan.formData.elevationData = JSON.parse(decompressed);
          
          // Clean up compression artifacts
          delete plan.formData._compressedElevationData;
          delete plan.formData._elevationCompressed;
          delete plan.formData._elevationLength;
          
          console.log(`Successfully decompressed elevation data with ${plan.formData.elevationData.length} points`);
        } catch (compressionError) {
          console.error('Error decompressing elevation data:', compressionError);
          // Set empty array so the application can still function
          plan.formData.elevationData = [];
        }
      }
      
      console.log(`Loaded race plan "${plan.formData?.raceName}" from localStorage`);
      return plan;
    }
    
    console.warn(`Race plan with ID ${id} not found in localStorage`);
    return null;
  } catch (error) {
    console.error('Error loading race plan by ID from localStorage:', error);
    return null;
  }
};

/**
 * Delete a race plan from localStorage
 * 
 * @param {string} id - The ID of the race plan to delete
 * @returns {boolean} - Whether the deletion was successful
 */
export const deleteRacePlan = (id) => {
  try {
    const plans = loadRacePlans();
    const filteredPlans = plans.filter(p => p.id !== id);
    
    localStorage.setItem('racePlans', JSON.stringify(filteredPlans));
    console.log(`Race plan with ID ${id} deleted from localStorage`);
    return true;
  } catch (error) {
    console.error('Error deleting race plan from localStorage:', error);
    return false;
  }
};

/**
 * Check if localStorage is available in the current browser
 * 
 * @returns {boolean} - Whether localStorage is available
 */
export const isLocalStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Get storage usage information for localStorage
 * 
 * @returns {Object} - Storage usage information
 */
export const getStorageUsage = () => {
  try {
    // Check if localStorage is available
    if (!isLocalStorageAvailable()) {
      return {
        available: false,
        usedMB: 0,
        quotaMB: 0,
        usedPercent: 0,
        planCount: 0
      };
    }
    
    // The estimated max size for localStorage in most browsers is 5MB
    const estimatedQuotaMB = 5;
    
    // Get all localStorage content
    let totalSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      totalSize += (key.length + value.length) * 2; // UTF-16 uses 2 bytes per character
    }
    
    // Calculate usage metrics
    const totalSizeInMB = totalSize / (1024 * 1024);
    const usedPercentage = (totalSizeInMB / estimatedQuotaMB) * 100;
    
    // Get race plans info
    const plans = loadRacePlans();
    const planCount = plans.length;
    
    return {
      available: true,
      usedMB: totalSizeInMB,
      quotaMB: estimatedQuotaMB, 
      usedPercent: usedPercentage,
      planCount: planCount
    };
  } catch (error) {
    console.error('Error checking storage usage:', error);
    return {
      available: false,
      usedMB: 0,
      quotaMB: 0,
      usedPercent: 0,
      planCount: 0
    };
  }
};

/**
 * Check if a race plan with the same name already exists
 * 
 * @param {string} raceName - The name of the race to check
 * @param {string} currentPlanId - The ID of the current plan (to exclude from comparison)
 * @returns {boolean} - Whether a plan with the same name exists
 */
export const doesRacePlanExist = (raceName, currentPlanId = null) => {
  try {
    if (!raceName) return false;
    
    const plans = loadRacePlans();
    const existingPlan = plans.find(plan => 
      plan.formData.raceName === raceName && 
      (!currentPlanId || plan.id !== currentPlanId)
    );
    
    return !!existingPlan;
  } catch (error) {
    console.error('Error checking if race plan exists:', error);
    return false;
  }
};

/**
 * Convert a byte array to a base64 string in chunks to avoid stack overflow
 */
const byteArrayToBase64 = (bytes) => {
  const chunks = [];
  const chunkSize = 0x8000; // Process 32KB chunks
  
  // Convert each chunk to a binary string
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    const binaryString = String.fromCharCode.apply(null, chunk);
    chunks.push(binaryString);
  }
  
  // Join chunks and convert to base64
  const fullString = chunks.join('');
  return btoa(fullString);
};

/**
 * Encode a race plan for sharing via URL
 * 
 * @param {Object} plan - The race plan to encode
 * @returns {string} - URL-safe encoded string
 */
export const encodeRacePlanForSharing = (plan) => {
  try {
    // Create a clean copy with only essential data for sharing (no elevation data)
    const sharedPlan = {
      formData: {
        ...plan.formData,
        // Exclude elevation data completely
        elevationData: [],
        gpxData: null,
        // Keep essential race details
        raceName: plan.formData.raceName,
        distance: plan.formData.distance,
        distanceUnit: plan.formData.distanceUnit,
        cutoffTime: plan.formData.cutoffTime,
        startTimeHours: plan.formData.startTimeHours,
        startTimeMinutes: plan.formData.startTimeMinutes,
        startTimeAMPM: plan.formData.startTimeAMPM,
        // Keep aid stations for segment plan
        aidStations: plan.formData.aidStations,
        aidStationUnit: plan.formData.aidStationUnit,
        // Keep drop bag related items
        startItems: plan.formData.startItems,
      },
      // Include complete nutrition plan
      nutritionPlan: plan.nutritionPlan,
      // Include complete pacing data
      paces: plan.paces,
      sharedPlan: true,
      readOnly: true, // Mark as read-only
      sharedAt: new Date().toISOString()
    };
    
    // Convert to JSON string
    const jsonString = JSON.stringify(sharedPlan);
    console.log('Original JSON length:', jsonString.length);
    
    // Convert string to UTF-8 bytes
    const textEncoder = new TextEncoder();
    const bytes = textEncoder.encode(jsonString);
    
    // Compress the bytes using pako
    const compressed = pako.deflate(bytes, { level: 9 });
    
    // Convert compressed bytes to base64 in chunks
    const base64 = byteArrayToBase64(compressed);
    
    // Make base64 URL safe
    const urlSafe = base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    
    console.log('Compressed length:', urlSafe.length);
    return urlSafe;
  } catch (error) {
    console.error('Error encoding race plan for sharing:', error);
    return null;
  }
};

/**
 * Decode a shared race plan from URL
 * 
 * @param {string} encodedPlan - URL-safe encoded string
 * @returns {Object} - Decoded race plan
 */
export const decodeSharedRacePlan = (encodedPlan) => {
  try {
    // Restore base64 padding
    let base64 = encodedPlan
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }
    
    // Convert base64 to bytes
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Decompress bytes
    const decompressed = pako.inflate(bytes);
    
    // Convert bytes back to string
    const jsonString = new TextDecoder().decode(decompressed);
    
    // Log the length of the decoded string for debugging
    console.log('Decoded JSON string length:', jsonString.length);
    console.log('JSON string preview:', jsonString.substring(0, 200));
    
    // Parse the JSON
    const plan = JSON.parse(jsonString);
    
    // Ensure this is a valid plan
    if (!plan || !plan.formData) {
      throw new Error('Invalid shared plan format');
    }
    
    // Create a clean plan object
    const cleanPlan = {
      id: `shared_${Date.now()}`,
      isShared: true,
      sharedPlan: true,
      readOnly: true,  // Mark as read-only
      sharedAt: plan.sharedAt || new Date().toISOString(),
      formData: {
        ...plan.formData,
        // Ensure we're not trying to load non-existent elevation data
        elevationData: [],
        gpxData: null
      },
      // Keep the nutrition plan
      nutritionPlan: plan.nutritionPlan || { segments: [] },
      // Keep the pacing data
      paces: plan.paces || {}
    };
    
    return cleanPlan;
  } catch (error) {
    console.error('Error decoding shared race plan:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      encodedLength: encodedPlan ? encodedPlan.length : 0
    });
    return null;
  }
};

/**
 * Sanitize a JSON string to fix common issues before parsing
 * 
 * @param {string} jsonString - The JSON string to sanitize
 * @returns {string} - Sanitized JSON string
 */
const sanitizeJsonString = (jsonString) => {
  if (!jsonString) return '';
  
  try {
    // Handle potential circular references or invalid representations
    return jsonString
      .replace(/,(\s*[\]}])/g, '$1') // Remove trailing commas
      .replace(/:\s*undefined/g, ': null') // Replace undefined with null
      .replace(/:\s*NaN/g, ': null') // Replace NaN with null
      .replace(/:\s*Infinity/g, ': null') // Replace Infinity with null
      .replace(/:\s*-Infinity/g, ': null'); // Replace -Infinity with null
  } catch (e) {
    console.error('Error sanitizing JSON string:', e);
    return jsonString; // Return original if sanitization fails
  }
};

/**
 * Deep copy of the nutrition plan to avoid reference issues
 * 
 * @param {Object} nutritionPlan - Original nutrition plan
 * @returns {Object} - Deep copy of the nutrition plan
 */
const deepCopyNutritionPlan = (nutritionPlan) => {
  if (!nutritionPlan || typeof nutritionPlan !== 'object') {
    return { segments: [] };
  }
  
  try {
    // Create a safe copy with only essential properties
    const copy = {
      totalCalories: nutritionPlan.totalCalories || 0,
      totalCarbs: nutritionPlan.totalCarbs || 0,
      totalFat: nutritionPlan.totalFat || 0,
      totalProtein: nutritionPlan.totalProtein || 0,
      totalSodium: nutritionPlan.totalSodium || 0,
      totalFluid: nutritionPlan.totalFluid || 0,
      segments: []
    };
    
    // Safely copy segments
    if (Array.isArray(nutritionPlan.segments)) {
      copy.segments = nutritionPlan.segments.map(segment => {
        if (!segment || typeof segment !== 'object') return {};
        
        return {
          start: segment.start || 0,
          end: segment.end || 0,
          calories: segment.calories || 0,
          carbs: segment.carbs || 0,
          fat: segment.fat || 0,
          protein: segment.protein || 0,
          sodium: segment.sodium || 0,
          fluid: segment.fluid || 0,
          distance: segment.distance || 0,
          duration: segment.duration || 0,
          name: segment.name || '',
          foods: Array.isArray(segment.foods) ? [...segment.foods] : [],
        };
      });
    }
    
    return copy;
  } catch (error) {
    console.error('Error creating deep copy of nutrition plan:', error);
    return { segments: [] };
  }
};

/**
 * Deep copy of the paces object to avoid reference issues
 * 
 * @param {Object} paces - Original paces object
 * @returns {Object} - Deep copy of the paces object
 */
const deepCopyPaces = (paces) => {
  if (!paces || typeof paces !== 'object') {
    return {};
  }
  
  try {
    // Create a new object with safe copies of each pace type
    const copy = {};
    
    // Define pace types to copy
    const paceTypes = ['cutoffPace', 'goalPace', 'stretchPace'];
    
    paceTypes.forEach(type => {
      if (paces[type] && typeof paces[type] === 'object') {
        copy[type] = {
          pace: paces[type].pace || '',
          mileMinutes: paces[type].mileMinutes || 0,
          kmMinutes: paces[type].kmMinutes || 0,
          finishTime: paces[type].finishTime || '',
          splits: Array.isArray(paces[type].splits) ? 
            [...paces[type].splits] : []
        };
      }
    });
    
    return copy;
  } catch (error) {
    console.error('Error creating deep copy of paces:', error);
    return {};
  }
};

/**
 * Simplify elevation data if it's too large
 * This prevents the URL from becoming too long
 * 
 * @param {Array} elevationData - The original elevation data array
 * @returns {Array} - Simplified elevation data or original if small enough
 */
const simplifyElevationDataIfNeeded = (elevationData) => {
  if (!elevationData || elevationData.length === 0) {
    return [];
  }
  
  // If elevation data is manageable size, return as is
  if (elevationData.length <= 100) {
    return elevationData;
  }
  
  // Otherwise, sample the data to reduce size
  const result = [];
  const step = Math.ceil(elevationData.length / 100);
  
  for (let i = 0; i < elevationData.length; i += step) {
    result.push(elevationData[i]);
  }
  
  // Always include the last point
  if (result[result.length - 1] !== elevationData[elevationData.length - 1]) {
    result.push(elevationData[elevationData.length - 1]);
  }
  
  console.log(`Simplified elevation data from ${elevationData.length} to ${result.length} points`);
  return result;
};

/**
 * Save a reference to a cloud-stored plan
 * @param {string} planId - The ID of the plan
 * @param {string} name - The name of the plan
 * @param {string} cloudUrl - The URL to access the plan
 */
export const saveCloudPlanReference = (planId, name, cloudUrl) => {
  try {
    // Get existing refs
    const refs = getCloudPlanRefs();
    
    // Create new reference
    const newRef = {
      id: planId,
      name: name,
      cloudUrl: cloudUrl,
      lastAccessed: new Date().toISOString()
    };
    
    // Check if plan with same ID already exists
    const existingIndex = refs.findIndex(ref => ref.id === planId);
    
    if (existingIndex >= 0) {
      // Update existing reference
      refs[existingIndex] = newRef;
    } else {
      // Add new reference
      refs.push(newRef);
    }
    
    // Sort by most recent
    refs.sort((a, b) => new Date(b.lastAccessed) - new Date(a.lastAccessed));
    
    // Get plan limits
    const { limit } = getUserPlanLimits();
    
    // Keep only the most recent ones up to the limit
    const limitedRefs = refs.slice(0, limit);
    
    // Save to localStorage
    localStorage.setItem('cloud_plan_refs', JSON.stringify(limitedRefs));
    
    console.log('Saved cloud plan reference:', newRef);
    return true;
  } catch (error) {
    console.error('Error saving cloud plan reference:', error);
    return false;
  }
};

/**
 * Get all cloud plan references from localStorage
 * 
 * @returns {Array} - Array of cloud plan references
 */
export const getCloudPlanRefs = () => {
  try {
    console.log('Getting cloud plan refs from localStorage');
    // Retrieve the references from localStorage
    const refsStr = localStorage.getItem('cloud_plan_refs');
    console.log('Raw cloud_plan_refs string:', refsStr);
    
    // Check if the string is valid JSON
    if (!refsStr || refsStr === 'undefined' || refsStr === 'null') {
      console.log('No cloud plan refs found in localStorage');
      return [];
    }
    
    try {
      // Try to parse the references
      const refs = JSON.parse(refsStr);
      
      // Make sure refs is actually an array
      if (!Array.isArray(refs)) {
        console.warn('cloud_plan_refs is not an array, resetting to []');
        localStorage.setItem('cloud_plan_refs', '[]');
        return [];
      }
      
      console.log(`Retrieved ${refs.length} cloud plan refs:`, refs);
      return refs;
    } catch (parseError) {
      console.error('Error parsing cloud plan refs:', parseError);
      // If there was an error parsing, reset the storage
      localStorage.setItem('cloud_plan_refs', '[]');
      return [];
    }
  } catch (error) {
    console.error('Error getting cloud plan refs:', error);
    return [];
  }
};

/**
 * Remove a cloud plan reference
 * 
 * @param {string} planId - The ID of the plan to remove
 * @returns {boolean} - Whether the reference was removed successfully
 */
const removeCloudPlanRef = (planId) => {
  try {
    const cloudRefs = getCloudPlanRefs();
    const filteredRefs = cloudRefs.filter(ref => ref.id !== planId);
    localStorage.setItem('cloud_plan_refs', JSON.stringify(filteredRefs));
    return true;
  } catch (error) {
    console.error('Error removing cloud plan reference:', error);
    return false;
  }
};

/**
 * Check if a race plan with the same name exists in either local or cloud storage
 * 
 * @param {string} raceName - The name to check
 * @param {string} currentPlanId - The ID of the current plan (to exclude from comparison)
 * @returns {Promise<{exists: boolean, location: string}>} - Whether the plan exists and where
 */
export const checkPlanNameExists = async (raceName, currentPlanId = null) => {
  try {
    if (!raceName) return { exists: false };
    
    // Check local storage
    const localPlans = loadRacePlans();
    const existingLocalPlan = localPlans.find(plan => 
      plan.formData.raceName === raceName && 
      (!currentPlanId || plan.id !== currentPlanId)
    );
    
    if (existingLocalPlan) {
      return { exists: true, location: 'local' };
    }
    
    // Check cloud storage
    const cloudRefs = getCloudPlanRefs();
    const existingCloudPlan = cloudRefs.find(ref => 
      ref.name === raceName && 
      (!currentPlanId || ref.id !== currentPlanId)
    );
    
    if (existingCloudPlan) {
      return { exists: true, location: 'cloud' };
    }
    
    return { exists: false };
  } catch (error) {
    console.error('Error checking plan name:', error);
    return { exists: false };
  }
};

/**
 * Save a plan to cloud storage
 * @param {Object} plan - The plan to save
 * @param {boolean} overwrite - Whether to overwrite an existing plan with the same name
 * @param {string} planIdToReplace - Optional ID of a specific plan to replace
 * @param {Function} getIdToken - Optional function to get Firebase ID token for authentication
 * @returns {Promise<string>} The URL of the saved plan
 */
export const savePlanToCloud = async (plan, overwrite = false, planIdToReplace = null, getIdToken = null) => {
  try {
    console.log('Saving plan to cloud storage:', {
      planName: plan.formData?.raceName,
      overwrite,
      planIdToReplace
    });
    
    // Always use POST method since server doesn't support PUT
    let url = '/api/plans';
    
    // If we have a specific planIdToReplace, include it in the request body instead
    if (planIdToReplace) {
      console.log(`Including planIdToReplace: ${planIdToReplace} in request body`);
    }
    
    // Create a copy of the plan for saving to strip unnecessary properties
    const planToSave = {
      ...plan,
      formData: { ...plan.formData },
      nutritionPlan: plan.nutritionPlan,
      paces: plan.paces,
      savedToCloud: true,
      lastModified: new Date().toISOString()
    };
    
    // Make sure we don't have any "readOnly" or related flags
    delete planToSave.readOnly;
    delete planToSave.isSharedPlan;
    delete planToSave.sharedPlan;
    delete planToSave.isShared;
    
    // Set a flag to indicate this is a cloud-stored plan
    planToSave.isCloudStored = true;

    // Get auth headers if user is authenticated
    const authHeaders = await getAuthHeaders(getIdToken);
    
    // Always use POST method with appropriate body parameters
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify({
        plan: planToSave,
        overwrite: overwrite,
        planIdToReplace: planIdToReplace // Include the ID to replace if needed
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Server responded with ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Update local cloud reference
    const raceName = plan.formData?.raceName?.trim() || 'Unnamed Race';
    const planId = data.id;
    const cloudUrl = `${window.location.origin}/api/plans/${planId}`;
    
    // Save the reference to localStorage
    saveCloudPlanReference(planId, raceName, cloudUrl);
    
    // Update local cloud storage info
    getUserPlanLimits(true); // Force refresh
    
    console.log(`Plan saved to cloud with ID: ${planId}, URL: ${cloudUrl}`);
    
    return cloudUrl;
  } catch (error) {
    console.error('Error saving plan to cloud:', error);
    throw error;
  }
};

/**
 * Load a race plan from the cloud storage
 * 
 * @param {string} url - The URL of the stored plan
 * @param {Function} getIdToken - Optional function to get Firebase ID token for authentication
 * @returns {Promise<Object|null>} - The loaded plan or null if failed
 */
export const loadPlanFromCloud = async (url, getIdToken = null) => {
  try {
    console.log('Loading plan from cloud URL:', url);
    
    // Extract the plan ID from the URL, handling both formats
    let planId;
    if (url.includes('/share/')) {
      planId = url.split('/share/').pop();
    } else if (url.includes('/api/plans/')) {
      planId = url.split('/api/plans/').pop();
    } else {
      // Just try to get the last segment as ID
      planId = url.split('/').pop();
    }
    
    console.log('Extracted plan ID:', planId);
    
    if (!planId) {
      throw new Error('Could not extract plan ID from URL');
    }
    
    // Get auth headers if user is authenticated
    const authHeaders = await getAuthHeaders(getIdToken);
    
    // Fetch the plan from the API
    console.log(`Making fetch request to: /api/plans/${planId}`);
    const response = await fetch(`/api/plans/${planId}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...authHeaders
      }
    });
    
    if (!response.ok) {
      console.error('Failed to fetch plan from API:', response.status);
      return null;
    }
    
    const rawPlan = await response.json();
    console.log('Raw API response:', {
      hasReadOnly: 'readOnly' in rawPlan,
      readOnly: rawPlan.readOnly,
      isSharedPlan: rawPlan.isSharedPlan,
      isShared: rawPlan.isShared,
      hasFormData: !!rawPlan.formData,
      planKeys: Object.keys(rawPlan)
    });
    
    // Create a clean copy of the plan
    const plan = JSON.parse(JSON.stringify(rawPlan));
    
    // FORCE REMOVE all read-only related flags
    plan.readOnly = false;
    plan.isSharedPlan = false;
    plan.sharedPlan = false;
    plan.isShared = false;
    plan.isEditable = true;
    
    // Delete these flags too to ensure they're not hiding somewhere
    delete plan.readOnly;
    delete plan.isSharedPlan;
    delete plan.isShared;
    delete plan.sharedPlan;
    
    // Add metadata to indicate this is a cloud-stored plan
    plan.isCloudStored = true;
    plan.cloudUrl = url;
    plan.id = planId;
    
    console.log('Prepared cloud plan with flags:', {
      id: plan.id,
      isCloudStored: plan.isCloudStored,
      readOnly: plan.readOnly,
      isSharedPlan: plan.isSharedPlan,
      isShared: plan.isShared,
      hasReadOnly: 'readOnly' in plan,
      hasIsSharedPlan: 'isSharedPlan' in plan
    });
    
    return plan;
  } catch (error) {
    console.error('Error loading plan from cloud:', error);
    return null;
  }
};

/**
 * Clear invalid cloud plan references
 * This removes references to plans that can't be loaded
 */
const clearInvalidCloudRefs = async () => {
  try {
    const cloudRefs = getCloudPlanRefs();
    console.log('Clearing invalid cloud refs, starting with:', cloudRefs.length);
    
    if (!cloudRefs || cloudRefs.length === 0) {
      console.log('No cloud refs found to validate');
      return [];
    }
    
    const validRefs = [];
    
    // Test each reference
    for (const ref of cloudRefs) {
      try {
        if (!ref || !ref.id || !ref.cloudUrl) {
          console.log('Skipping invalid ref format:', ref);
          continue;
        }
        
        console.log('Validating cloud ref:', ref.id, ref.name);
        
        // Use a timeout to prevent hanging on network requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        try {
          // Check if the plan exists by making a HEAD request instead of loading the full plan
          const response = await fetch(`/api/plans/${ref.id}`, { 
            method: 'HEAD',
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            console.log('Validated ref as valid:', ref.id);
            validRefs.push(ref);
          } else {
            console.log(`Removing invalid cloud ref (HTTP ${response.status}):`, ref.id);
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            console.log(`Validation timed out for ref ${ref.id}, removing`);
          } else {
            console.error(`Network error validating ref ${ref.id}:`, fetchError);
          }
        }
      } catch (error) {
        console.error(`Error validating cloud ref ${ref?.id}:`, error);
      }
    }
    
    console.log('Validated cloud refs:', validRefs.length, 'of', cloudRefs.length);
    
    // Save only valid references
    localStorage.setItem('cloud_plan_refs', JSON.stringify(validRefs));
    return validRefs;
  } catch (error) {
    console.error('Error clearing invalid cloud refs:', error);
    return [];
  }
};

/**
 * Load all race plans (both local and cloud-stored)
 * 
 * @returns {Promise<Array>} - Array of all race plans
 */
export const loadAllRacePlans = async () => {
  try {
    // Get local plans
    const localPlans = loadRacePlans();
    console.log('Loaded local plans:', localPlans.length);
    
    // Get cloud plan references directly - don't clear them first
    let cloudRefs = getCloudPlanRefs();
    console.log('Cloud plan refs before processing:', cloudRefs.length, cloudRefs);
    
    // Filter out invalid refs and fix URLs if needed
    cloudRefs = cloudRefs.filter(ref => {
      // Make sure ref has all required fields
      if (!ref || !ref.id || !ref.name) {
        console.log('Filtering out invalid ref (missing required fields):', ref);
        return false;
      }
      
      // Make sure cloudUrl is set and valid
      if (!ref.cloudUrl) {
        console.log('Adding missing cloudUrl for ref:', ref.id);
        ref.cloudUrl = `${window.location.origin}/api/plans/${ref.id}`;
      } else if (ref.cloudUrl.includes('/share/') && !ref.cloudUrl.includes('/api/plans/')) {
        // Convert old-style URLs to new format
        console.log('Converting old URL format to new:', ref.cloudUrl);
        const planId = ref.cloudUrl.split('/share/').pop();
        ref.cloudUrl = `${window.location.origin}/api/plans/${planId}`;
      }
      
      return true;
    });
    
    console.log('Cloud plan refs after processing:', cloudRefs.length, cloudRefs);
    
    if (!cloudRefs || cloudRefs.length === 0) {
      console.log('No cloud refs found, returning only local plans');
      return localPlans;
    }
    
    // Save processed refs back to storage
    localStorage.setItem('cloud_plan_refs', JSON.stringify(cloudRefs));
    
    // Load cloud plans with a timeout to prevent hanging
    const cloudPlanPromises = cloudRefs.map(ref => {
      return new Promise(async (resolve) => {
        try {
          console.log('Loading cloud plan:', ref.id, ref.name, 'URL:', ref.cloudUrl);
          // Set a timeout of 8 seconds for loading each cloud plan
          const controller = new AbortController();
          const timeoutId = setTimeout(() => {
            controller.abort();
            console.log(`Timeout loading cloud plan ${ref.id}`);
          }, 8000);
          
          // Extract the plan ID from the URL
          let planId = ref.id;
          if (!planId && ref.cloudUrl) {
            if (ref.cloudUrl.includes('/api/plans/')) {
              planId = ref.cloudUrl.split('/api/plans/').pop();
            } else if (ref.cloudUrl.includes('/share/')) {
              planId = ref.cloudUrl.split('/share/').pop();
            }
          }
          
          if (!planId) {
            console.error('Could not determine plan ID for cloud plan:', ref);
            clearTimeout(timeoutId);
            resolve(null);
            return;
          }
          
          // Try to load the plan with the abort controller
          // Make sure we're using the correct URL format
          const apiUrl = `/api/plans/${planId}`;
          console.log(`Fetching cloud plan from: ${apiUrl}`);
          
          // Try multiple times with exponential backoff
          let attempt = 0;
          const maxAttempts = 3;
          let success = false;
          let plan = null;
          
          while (attempt < maxAttempts && !success) {
            try {
              const response = await fetch(apiUrl, {
                signal: controller.signal,
                headers: {
                  'Cache-Control': 'no-cache',
                  'Pragma': 'no-cache'
                }
              });
              
              if (!response.ok) {
                console.warn(`Failed to load cloud plan ${planId} on attempt ${attempt+1}: HTTP ${response.status}`);
                attempt++;
                if (attempt < maxAttempts) {
                  // Wait with exponential backoff
                  const delay = Math.pow(2, attempt) * 500;
                  console.log(`Retrying in ${delay}ms...`);
                  await new Promise(r => setTimeout(r, delay));
                }
                continue;
              }
              
              plan = await response.json();
              
              if (plan && typeof plan === 'object') {
                success = true;
                console.log(`Successfully loaded cloud plan on attempt ${attempt+1}:`, plan.formData?.raceName || ref.name);
              } else {
                console.warn(`Failed to load cloud plan ${planId}, invalid data:`, plan);
                attempt++;
                if (attempt < maxAttempts) {
                  const delay = Math.pow(2, attempt) * 500;
                  await new Promise(r => setTimeout(r, delay));
                }
              }
            } catch (fetchError) {
              console.error(`Error fetching cloud plan ${planId} on attempt ${attempt+1}:`, fetchError);
              attempt++;
              if (attempt < maxAttempts) {
                const delay = Math.pow(2, attempt) * 500;
                await new Promise(r => setTimeout(r, delay));
              }
            }
          }
          
          clearTimeout(timeoutId);
          
          if (success && plan) {
            // Ensure the plan has a properly structured formData
            if (!plan.formData) {
              console.warn(`Cloud plan ${planId} has missing formData, skipping`);
              resolve(null);
              return;
            }
            
            // Add metadata from the reference
            plan.isCloudStored = true;
            plan.lastAccessed = ref.lastAccessed;
            plan.cloudUrl = ref.cloudUrl || `${window.location.origin}/api/plans/${planId}`;
            plan.id = planId;
            
            // Ensure cloud plans are properly marked as editable
            plan.readOnly = false;
            plan.isSharedPlan = false;
            plan.isEditable = true;
            
            // Remove any shared-related flags
            delete plan.sharedPlan;
            delete plan.isShared;
            
            console.log('Successfully loaded and prepared cloud plan:', plan.formData?.raceName || ref.name);
            resolve(plan);
          } else {
            console.warn(`Failed to load cloud plan ${planId} after ${maxAttempts} attempts`);
            resolve(null);
          }
        } catch (error) {
          console.error(`Error loading cloud plan ${ref.id}:`, error);
          resolve(null);
        }
      });
    });
    
    // Load all cloud plans with a Promise.all
    const cloudPlans = await Promise.all(cloudPlanPromises);
    
    // Filter out any failed cloud plan loads
    const validCloudPlans = cloudPlans.filter(plan => plan !== null);
    console.log('Loaded valid cloud plans:', validCloudPlans.length, 'of', cloudRefs.length, validCloudPlans);
    
    // If we loaded fewer plans than refs, update the valid refs
    if (validCloudPlans.length < cloudRefs.length) {
      const successfulIds = validCloudPlans.map(plan => plan.id);
      const updatedRefs = cloudRefs.filter(ref => successfulIds.includes(ref.id));
      localStorage.setItem('cloud_plan_refs', JSON.stringify(updatedRefs));
      console.log('Updated cloud refs to match successfully loaded plans:', updatedRefs.length);
    }
    
    // Combine and sort by last accessed
    const allPlans = [...localPlans, ...validCloudPlans].sort((a, b) => {
      const aTime = a.lastAccessed || a.savedAt || a.timestamp;
      const bTime = b.lastAccessed || b.savedAt || b.timestamp;
      return new Date(bTime) - new Date(aTime);
    });
    
    console.log('Total plans loaded:', allPlans.length, 'local:', localPlans.length, 'cloud:', validCloudPlans.length);
    return allPlans;
  } catch (error) {
    console.error('Error loading all race plans:', error);
    // In case of error, return at least local plans
    return loadRacePlans();
  }
};

/**
 * Delete a race plan (both local and cloud-stored)
 * 
 * @param {string} id - The ID of the race plan to delete
 * @param {Function} getIdToken - Optional function to get Firebase ID token for authentication
 * @returns {Promise<boolean>} - Whether the deletion was successful
 */
export const deleteRacePlanWithCloud = async (id, getIdToken = null) => {
  try {
    // Try to delete from localStorage first
    const localSuccess = deleteRacePlan(id);
    
    // Try to delete from cloud if it exists
    const cloudRefs = getCloudPlanRefs();
    const cloudRef = cloudRefs.find(ref => ref.id === id);
    
    // For authenticated users, always try to delete from cloud even if no local cloudRef
    // For anonymous users, only delete if cloudRef exists
    const shouldDeleteFromCloud = cloudRef || (getIdToken !== null);
    
    if (shouldDeleteFromCloud) {
      try {
        // Get auth headers if user is authenticated
        const authHeaders = await getAuthHeaders(getIdToken);
        
        // Make a request to delete from cloud storage
        const response = await fetch(`/api/plans/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          }
        });
        
        if (response.ok) {
          // Remove from local cloudRef list if it exists
          if (cloudRef) {
            removeCloudPlanRef(id);
          }
        } else {
          throw new Error(`Failed to delete cloud plan: ${response.status}`);
        }
      } catch (error) {
        console.error('Error deleting plan from cloud:', error);
        throw error;
      }
    }
    
    return localSuccess;
  } catch (error) {
    console.error('Error deleting race plan:', error);
    return false;
  }
};

/**
 * Get the user's plan limits for cloud storage
 * @param {boolean} forceRefresh - Whether to force a refresh of the limits
 * @returns {Object} The user's plan limits
 */
export const getUserPlanLimits = (forceRefresh = false) => {
  // Get limit from centralized config
  const CLOUD_STORAGE_LIMIT = config.cloudStorage.maxPlans;
  const isUnlimited = config.cloudStorage.isUnlimited;
  
  // Just use the count of existing cloud plan references
  const cloudRefs = getCloudPlanRefs();
  
  return {
    used: cloudRefs.length,
    limit: isUnlimited ? Infinity : CLOUD_STORAGE_LIMIT,
    displayLimit: config.cloudStorage.displayLimit,
    canSaveMore: isUnlimited ? true : cloudRefs.length < CLOUD_STORAGE_LIMIT,
    isUnlimited: isUnlimited
  };
};

/**
 * Get information about cloud storage usage
 * @returns {Object} Information about cloud storage usage
 */
export const getCloudStorageInfo = () => {
  const { used, limit, displayLimit, canSaveMore, isUnlimited } = getUserPlanLimits();
  const planRefs = getCloudPlanRefs();
  
  return {
    used,
    limit,
    displayLimit,
    canSaveMore,
    isUnlimited,
    // Include the actual plans
    plans: planRefs.map(ref => ({
      id: ref.id,
      name: ref.name,
      lastAccessed: ref.lastAccessed,
      cloudUrl: ref.cloudUrl
    }))
  };
};

/**
 * Get the count of cloud-stored plans
 * 
 * @returns {number} - The number of cloud-stored plans
 */
export const getCloudPlanCount = () => {
  try {
    const refs = getCloudPlanRefs();
    console.log(`Current cloud plan count: ${refs.length}`);
    
    // Filter out invalid references
    const validRefs = refs.filter(ref => ref && ref.id && ref.cloudUrl);
    
    if (refs.length !== validRefs.length) {
      console.log(`Filtered out ${refs.length - validRefs.length} invalid cloud plan references`);
      // Update the stored references if we found invalid ones
      localStorage.setItem('cloud_plan_refs', JSON.stringify(validRefs));
    }
    
    return validRefs.length;
  } catch (error) {
    console.error('Error getting cloud plan count:', error);
    return 0;
  }
};

/**
 * Load all plans for an authenticated user from the cloud
 * 
 * @param {Function} getIdToken - Function to get Firebase ID token
 * @returns {Promise<Array>} - Array of user's plans
 */
export const loadAuthenticatedPlans = async (getIdToken) => {
  try {
    if (!getIdToken) {
      console.log('No getIdToken function provided, cannot load authenticated plans');
      return [];
    }

    const token = await getIdToken();
    if (!token) {
      console.log('No auth token available, cannot load authenticated plans');
      return [];
    }

    console.log('Loading authenticated plans from cloud...');
    const response = await fetch('/api/plans/list', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load plans: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`Loaded ${data.plans?.length || 0} authenticated plans from cloud`);
    return data.plans || [];
  } catch (error) {
    console.error('Error loading authenticated plans:', error);
    return [];
  }
}; 